<template>
  <VLayout>
    <VFlex>
      <div class="password-component">
        <VTextField
          ref="password"
          v-model="password"
          class="flex-column password-field"
          :append-icon="showPassword ? 'show' : 'hide'"
          :rules="passwordRules"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          required
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        >
          <template slot="label">
            <span class="field-label">{{ txc.newPassword }}</span>
          </template>
          <template slot="append">
            <span
              class="field-append-label"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? txc.hide : txc.show }}
            </span>
          </template>
        </VTextField>
        <div v-show="criteriaBlock" class="criteria-block">
          <ul>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includes8Chars }"
            >
              <NsIcon
                v-if="!includes8Chars"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includes8Chars"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includes8Chars }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesUpper }"
            >
              <NsIcon
                v-if="!includesUpper"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesUpper"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesUpper }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesLower }"
            >
              <NsIcon
                v-if="!includesLower"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesLower"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesLower }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesNumber }"
            >
              <NsIcon
                v-if="!includesNumber"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesNumber"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesNumber }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesSpecialChars }"
            >
              <NsIcon
                v-if="!includesSpecialChars"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesSpecialChars"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesSpecialChars }}</div>
            </li>
          </ul>
          <ul v-show="criteriaComplete" class="criteria-complete">
            <li class="completed">
              <NsIcon
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.criteriaComplete }}</div>
            </li>
          </ul>
        </div>
      </div>
    </VFlex>
  </VLayout>
</template>

<script>
/**
 * Original Author: Ben Layton
 * Original Team: Fore oh Four!
 */
import { VLayout, VFlex, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  name: 'NsPassword',
  components: {
    VLayout,
    VTextField,
    VFlex,
    NsIcon,
  },
  props: {
    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default: () => {
        return {
          newPassword: 'New Password',
          passwordRequired: 'Password is required',
          excludesWhitespaceChars: 'Password must not include spaces',
          includes8Chars: 'Use 8 or more characters',
          includesUpper: 'Use an upper case letter (e.g. A)',
          includesLower: 'Use a lower case letter (e.g. a)',
          includesNumber: 'Use a number (e.g. 1234)',
          includesSpecialChars: 'Use a symbol (e.g. !@#$)',
          criteriaComplete: 'Your Password is STRONG',
          passwordConditionsMet: "Password doesn't meet minimum requirements",
          hide: 'HIDE',
          show: 'SHOW',
        };
      },
    },
    /**
     * Validate using the password requirement criteria
     */
    criteriaOn: {
      type: Boolean,
      default: true,
    },
    /**
     * Show the password requirement criteria block
     */
    criteriaVisible: {
      type: Boolean,
      default: true,
    },
    /**
     * Number of password requirement criteria to meet
     */
    criteriaCount: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      includes8Chars: false,
      includesUpper: false,
      includesLower: false,
      includesNumber: false,
      includesSpecialChars: false,
      criteriaComplete: false,
      showCriteria: false,
      criteria: {
        excludesWhitespaceChars: {
          regex: /(?=^\S+$)/,
        },
        includes8Chars: {
          regex: /(?=.{8,})/,
        },
        includesUpper: {
          regex: /(?=.*[A-Z])/,
        },
        includesLower: {
          regex: /(?=.*[a-z])/,
        },
        includesNumber: {
          regex: /(?=.*[0-9])/,
        },
        includesSpecialChars: {
          regex: /(?=.*[@#$%^&*()_+?~!])/,
        },
      },
      password: '',
      showPassword: false,
    };
  },
  computed: {
    criteriaBlock() {
      return this.criteriaOn && this.criteriaVisible && this.showCriteria;
    },
    passwordRules() {
      // Grab everything up front to help Vue know our deps
      const { txc, criteria, showCriteria, criteriaOn } = this;
      if (!showCriteria && criteriaOn) {
        return [() => this.passwordValid() || txc.passwordConditionsMet];
      }
      return [
        (v) => !!v || txc.passwordRequired,
        // v => !this.criteriaComplete || txc.passwordConditionsMet
        (v) =>
          (v && criteria.excludesWhitespaceChars.regex.test(v)) ||
          txc.excludesWhitespaceChars,
        () => this.passwordValid() || txc.passwordConditionsMet,
        (v) => (v && v.length > 7) || txc.includes8Chars,
      ];
    },
    txc() {
      return this.translations;
    },
  },
  watch: {
    password() {
      this.includes8Chars =
        this.password &&
        this.password.match(this.criteria.includes8Chars.regex);
      this.includesUpper =
        this.password && this.password.match(this.criteria.includesUpper.regex);
      this.includesLower =
        this.password && this.password.match(this.criteria.includesLower.regex);
      this.includesNumber =
        this.password &&
        this.password.match(this.criteria.includesNumber.regex);
      this.includesSpecialChars =
        this.password &&
        this.password.match(this.criteria.includesSpecialChars.regex);
    },
  },
  methods: {
    onFocus() {
      this.showCriteria = true;
    },
    onBlur() {
      // TODO: why hide?
      this.showCriteria = false;
    },
    onInput() {
      /**
       * @event input
       * @type {string}
       */
      this.$emit('input', this.password);
      /**
       * @event password
       * @type {boolean}
       */
      this.$emit('valid', this.passwordValid());
    },
    passwordValid() {
      let totalCompleted = 0;
      // includesUpper
      if (
        this.password &&
        this.password.match(this.criteria.includesUpper.regex)
      ) {
        totalCompleted++;
      } else {
        totalCompleted += 0;
      }
      // includesLower
      if (
        this.password &&
        this.password.match(this.criteria.includesLower.regex)
      ) {
        totalCompleted++;
      } else {
        totalCompleted += 0;
      }
      // includesNumber
      if (
        this.password &&
        this.password.match(this.criteria.includesNumber.regex)
      ) {
        totalCompleted++;
      } else {
        totalCompleted += 0;
      }
      // includesSpecialChars
      if (
        this.password &&
        this.password.match(this.criteria.includesSpecialChars.regex)
      ) {
        totalCompleted++;
      } else {
        totalCompleted += 0;
      }
      // criteriaCount or more criteria and includes8Chars
      if (
        totalCompleted >= this.criteriaCount - 1 &&
        this.password.length > 7
      ) {
        this.criteriaComplete = true;
        return true;
      } else {
        this.criteriaComplete = false;
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";

::v-deep * {
  $color-dryink: map.get(typographyVariables.$typography, font-family-primary);
}
$color-dryink: map.get(colors.$baseColors, dryink);
$color-error: map.get(colors.$baseColors, 'color-error');
$color-success: map.get(colors.$baseColors, success);
.v-label,
.v-icon {
  &.primary--text {
    color: $color-dryink !important;
  }
}
.password-component {
  vertical-align: center;

  ul {
    text-align: left;
    list-style: none;
    padding-left: 0;
    line-height: 26px;
    margin-bottom: 20px;

    li {
      display: flex;
      align-items: center;

      i {
        font-size: 15px;
        margin-right: 5px;
      }

      &:not(.completed) {
        i {
          color: #63666a;
        }
      }
    }

    &.criteria-complete {
      margin-top: -30px;
    }
  }

  .completed {
    color: $color-success;
  }
  .v-text-field__suffix {
    &.primary--text {
      color: $color-dryink !important;
    }
  }
  .field-append-label {
    &:hover {
      cursor: pointer;
    }
  }
}

.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  font-size: 24px; /* Preferred icon size */
  display: inline-block;
  line-height: 1;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;

  /* Support for all WebKit browsers. */
  -webkit-font-smoothing: antialiased;
  /* Support for Safari and Chrome. */
  text-rendering: optimizeLegibility;

  /* Support for Firefox. */
  -moz-osx-font-smoothing: grayscale;

  /* Support for IE. */
  font-feature-settings: 'liga';
}
.v-text-field__suffix {
  right: 6px !important;
}
.v-text-field > .v-input__control > .v-input__slot:before {
  border: none;
}

.error--text {
  .field-append-label {
    color: $color-error;
  }
}
</style>
